import { useMemo, useState } from "react";
import { Beaker, Droplet, Info, Sparkles, BookOpen } from "lucide-react";
import ReconstitutionWalkthrough from "./ReconstitutionWalkthrough";

const VIAL_SIZES_MG = [2, 5, 10, 15, 20];
const BAC_OPTIONS_ML = [1, 2, 3, 5];
const IDEAL_UNITS = 25; // aim for a clean, easy-to-read draw around 25u

function toMcg(amount, unit) {
  if (amount == null || isNaN(amount)) return null;
  const u = (unit || "").toLowerCase();
  if (u === "mg") return amount * 1000;
  if (u === "mcg" || u === "ug") return amount;
  return null;
}

// Pick the vial/BAC combo whose draw is closest to IDEAL_UNITS while staying ≤ 100u.
function autoDeriveRecommendation(doseMcg) {
  if (!doseMcg || doseMcg <= 0) return null;
  let best = null;
  for (const mg of VIAL_SIZES_MG) {
    for (const ml of BAC_OPTIONS_ML) {
      const conc = (mg * 1000) / ml; // mcg/mL
      const units = (doseMcg / conc) * 100;
      if (units > 100 || units < 1) continue; // out of range for U-100
      const score = Math.abs(units - IDEAL_UNITS);
      if (!best || score < best.score) best = { mg, ml, units, score };
    }
  }
  return best ? { mg: best.mg, ml: best.ml, units: best.units, source: "auto" } : null;
}

export default function ReconstitutionGuide({ peptide }) {
  const [customDose, setCustomDose] = useState("");
  const [customUnit, setCustomUnit] = useState(peptide?.default_unit || "mcg");
  const [walkthroughOpen, setWalkthroughOpen] = useState(false);

  const activeDoseMcg = useMemo(() => {
    const c = parseFloat(customDose);
    if (!isNaN(c) && c > 0) return toMcg(c, customUnit);
    return toMcg(peptide?.default_dose, peptide?.default_unit);
  }, [customDose, customUnit, peptide]);

  const activeDoseLabel = useMemo(() => {
    const c = parseFloat(customDose);
    if (!isNaN(c) && c > 0) return `${c} ${customUnit}`;
    if (peptide?.default_dose != null) return `${peptide.default_dose} ${peptide.default_unit || "mcg"}`;
    return null;
  }, [customDose, customUnit, peptide]);

  const unitCompatible = activeDoseMcg != null;

  // Admin-set override wins over auto-derivation. Only applies when using the default dose (not a custom preview).
  const recommendation = useMemo(() => {
    if (!unitCompatible) return null;
    const usingDefaults = !customDose || isNaN(parseFloat(customDose));
    const overrideMg = peptide?.suggested_vial_mg;
    const overrideMl = peptide?.suggested_bac_water_ml;
    if (usingDefaults && overrideMg && overrideMl) {
      const conc = (overrideMg * 1000) / overrideMl;
      const units = (activeDoseMcg / conc) * 100;
      return { mg: overrideMg, ml: overrideMl, units, source: "admin" };
    }
    return autoDeriveRecommendation(activeDoseMcg);
  }, [unitCompatible, customDose, peptide, activeDoseMcg]);

  return (
    <div className="space-y-4" data-testid="reconstitution-guide">
      <div className="border border-slate-200 bg-slate-50 p-3">
        <div className="flex items-start gap-2">
          <Info className="w-3.5 h-3.5 text-[#0D9488] mt-0.5 shrink-0" />
          <p className="text-xs text-slate-600 leading-relaxed">
            Pick a vial size (row) and how much BAC water you'll mix it with (column). The number shown is the exact
            unit to draw on a <span className="text-slate-900 font-mono-num">U-100 insulin syringe</span> for a dose of
            {" "}
            <span className="text-[#0D9488] font-mono-num">{activeDoseLabel || "—"}</span>.
          </p>
        </div>
      </div>

      {recommendation && (
        <div className="border border-[#0D9488]/50 bg-[#0D9488]/5 p-3" data-testid="recon-recommendation-banner">
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-[#0D9488] mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-[#0D9488] mb-1">
                // {recommendation.source === "admin" ? "Recommended by Admin" : "Suggested Combo"}
              </div>
              <div className="font-mono-num text-sm text-slate-900 leading-snug">
                <span className="font-black text-[#0D9488]">{recommendation.mg} mg</span> vial
                {" + "}
                <span className="font-black text-[#0D9488]">{recommendation.ml} mL</span> BAC water
                {" → draw "}
                <span className="font-black text-[#0D9488]">{recommendation.units.toFixed(1)} u</span>
              </div>
              <button
                type="button"
                onClick={() => setWalkthroughOpen(true)}
                className="mt-2 inline-flex items-center gap-1.5 bg-[#0D9488] hover:bg-[#0F766E] text-black font-mono-num text-[10px] uppercase tracking-widest font-black px-3 h-7"
                data-testid="recon-open-walkthrough"
              >
                <BookOpen className="w-3 h-3" /> How to Reconstitute
              </button>
            </div>
          </div>
        </div>
      )}

      <ReconstitutionWalkthrough
        open={walkthroughOpen}
        onOpenChange={setWalkthroughOpen}
        peptide={peptide}
        vialMg={recommendation?.mg}
        bacMl={recommendation?.ml}
        drawUnits={recommendation?.units}
      />

      <div className="grid grid-cols-3 gap-2">
        <div className="col-span-2">
          <label className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-1">
            Custom dose (optional)
          </label>
          <input
            type="number"
            step="any"
            min="0"
            value={customDose}
            onChange={(e) => setCustomDose(e.target.value)}
            placeholder={peptide?.default_dose != null ? `Default: ${peptide.default_dose}` : "e.g. 250"}
            className="w-full bg-slate-50 border border-slate-200 rounded-none h-9 px-2 text-sm font-mono-num text-slate-900 focus:outline-none focus:border-[#0D9488]"
            data-testid="recon-custom-dose"
          />
        </div>
        <div>
          <label className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-1">Unit</label>
          <select
            value={customUnit}
            onChange={(e) => setCustomUnit(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-none h-9 px-2 text-sm font-mono-num text-slate-900 focus:outline-none focus:border-[#0D9488]"
            data-testid="recon-custom-unit"
          >
            <option value="mcg">mcg</option>
            <option value="mg">mg</option>
          </select>
        </div>
      </div>

      {!unitCompatible ? (
        <div className="border border-[#0D9488]/40 bg-[#0D9488]/5 text-[#0D9488] text-xs px-3 py-2 font-mono-num uppercase tracking-widest">
          Reconstitution math only supports mcg / mg doses. Set a custom dose above to preview.
        </div>
      ) : (
        <div className="border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="recon-matrix">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-3 py-2 font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500">
                  <div className="flex items-center gap-1.5">
                    <Beaker className="w-3 h-3 text-[#0D9488]" /> Vial
                  </div>
                </th>
                {BAC_OPTIONS_ML.map((ml) => (
                  <th key={ml} className="text-center px-3 py-2 font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500">
                    <div className="flex items-center justify-center gap-1.5">
                      <Droplet className="w-3 h-3 text-[#0D9488]" /> {ml} mL
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {VIAL_SIZES_MG.map((mg) => (
                <tr key={mg} className="border-b border-slate-200 last:border-b-0">
                  <td className="px-3 py-2 font-mono-num text-slate-900 font-black">{mg} mg</td>
                  {BAC_OPTIONS_ML.map((ml) => {
                    const concMcgPerMl = (mg * 1000) / ml;
                    const mlDraw = activeDoseMcg / concMcgPerMl;
                    const units = mlDraw * 100;
                    const overflow = units > 100;
                    const isRecommended = recommendation && recommendation.mg === mg && recommendation.ml === ml;
                    return (
                      <td
                        key={ml}
                        className={`text-center px-3 py-2 font-mono-num transition-colors ${
                          overflow
                            ? "text-[#FF3B30]"
                            : isRecommended
                            ? "text-black bg-[#0D9488] font-black"
                            : "text-[#0D9488]"
                        }`}
                        data-testid={`recon-cell-${mg}mg-${ml}ml`}
                        title={`${mlDraw.toFixed(3)} mL`}
                      >
                        <div className={`font-black text-base leading-none ${isRecommended ? "text-black" : ""}`}>
                          {units.toFixed(1)}
                        </div>
                        <div className={`text-[9px] uppercase tracking-widest mt-0.5 ${isRecommended ? "text-black/70" : "text-slate-500"}`}>
                          {isRecommended ? "recommended" : "u"}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {unitCompatible && (
        <div className="grid grid-cols-3 gap-2 text-[10px] font-mono-num uppercase tracking-widest text-slate-500">
          <div className="border border-slate-200 p-2">
            <div>Syringe</div>
            <div className="text-slate-800 mt-0.5 normal-case tracking-normal">U-100 insulin (1 mL = 100 u)</div>
          </div>
          <div className="border border-slate-200 p-2">
            <div>Formula</div>
            <div className="text-slate-800 mt-0.5 normal-case tracking-normal">dose ÷ concentration × 100</div>
          </div>
          <div className="border border-[#FF3B30]/40 p-2">
            <div className="text-[#FF3B30]">Red = &gt;100u</div>
            <div className="text-slate-800 mt-0.5 normal-case tracking-normal">won't fit on a U-100 syringe</div>
          </div>
        </div>
      )}
    </div>
  );
}
